/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ProductValidationStatusEnum } from './ProductValidationStatusEnum';
import {
    ProductValidationStatusEnumFromJSON,
    ProductValidationStatusEnumFromJSONTyped,
    ProductValidationStatusEnumToJSON,
} from './ProductValidationStatusEnum';
import type { CurrencyRate } from './CurrencyRate';
import {
    CurrencyRateFromJSON,
    CurrencyRateFromJSONTyped,
    CurrencyRateToJSON,
} from './CurrencyRate';
import type { Issuer } from './Issuer';
import {
    IssuerFromJSON,
    IssuerFromJSONTyped,
    IssuerToJSON,
} from './Issuer';
import type { ProductPermissionEnum } from './ProductPermissionEnum';
import {
    ProductPermissionEnumFromJSON,
    ProductPermissionEnumFromJSONTyped,
    ProductPermissionEnumToJSON,
} from './ProductPermissionEnum';
import type { Wallet } from './Wallet';
import {
    WalletFromJSON,
    WalletFromJSONTyped,
    WalletToJSON,
} from './Wallet';
import type { AdminVersionedIssuanceDocument } from './AdminVersionedIssuanceDocument';
import {
    AdminVersionedIssuanceDocumentFromJSON,
    AdminVersionedIssuanceDocumentFromJSONTyped,
    AdminVersionedIssuanceDocumentToJSON,
} from './AdminVersionedIssuanceDocument';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ProductTypeEnum } from './ProductTypeEnum';
import {
    ProductTypeEnumFromJSON,
    ProductTypeEnumFromJSONTyped,
    ProductTypeEnumToJSON,
} from './ProductTypeEnum';
import type { NetworkNameTechnicalEnum } from './NetworkNameTechnicalEnum';
import {
    NetworkNameTechnicalEnumFromJSON,
    NetworkNameTechnicalEnumFromJSONTyped,
    NetworkNameTechnicalEnumToJSON,
} from './NetworkNameTechnicalEnum';
import type { TokenContractInfo } from './TokenContractInfo';
import {
    TokenContractInfoFromJSON,
    TokenContractInfoFromJSONTyped,
    TokenContractInfoToJSON,
} from './TokenContractInfo';
import type { DistributionPlatform } from './DistributionPlatform';
import {
    DistributionPlatformFromJSON,
    DistributionPlatformFromJSONTyped,
    DistributionPlatformToJSON,
} from './DistributionPlatform';
import type { ProductStatusEnum } from './ProductStatusEnum';
import {
    ProductStatusEnumFromJSON,
    ProductStatusEnumFromJSONTyped,
    ProductStatusEnumToJSON,
} from './ProductStatusEnum';

/**
 * 
 * @export
 * @interface AdminProductDetail
 */
export interface AdminProductDetail {
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    agioPercent: string;
    /**
     * 
     * @type {Document}
     * @memberof AdminProductDetail
     */
    backToBack: Document;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    backupWallet?: string;
    /**
     * Defines whether commitment requires a bank account for investor
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    bankAccountRequired?: boolean;
    /**
     * Shall accrued interests be calculated for investments and shown in payment details?
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    calculateAccruedInterest?: boolean;
    /**
     * How many pieces of the digital representation equal one real-world asset.
     * @type {string}
     * @memberof AdminProductDetail
     */
    calculationFactor?: string | null;
    /**
     * Deadline for the investor to be able to cancel the investment (in days) after signing
     * @type {number}
     * @memberof AdminProductDetail
     */
    cancellationDeadline?: number;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    code?: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminProductDetail
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Array<CurrencyRate>}
     * @memberof AdminProductDetail
     */
    readonly currencies: Array<CurrencyRate>;
    /**
     * 
     * @type {Array<DistributionPlatform>}
     * @memberof AdminProductDetail
     */
    readonly distributionPlatforms: Array<DistributionPlatform>;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    readonly entryType: string;
    /**
     * This is a product with an eWpG issuance. That means we need to create registry statements
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    ewpg?: boolean;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    readonly id: string;
    /**
     * Deadline for the investor making the bank transfer (in days)
     * @type {number}
     * @memberof AdminProductDetail
     */
    investmentPaymentDeadline: number;
    /**
     * Defines whether this token complies with ECSP.
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    isEcsp?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    readonly isTokenized: boolean;
    /**
     * ISIN of security
     * @type {string}
     * @memberof AdminProductDetail
     */
    isin?: string | null;
    /**
     * 
     * @type {AdminVersionedIssuanceDocument}
     * @memberof AdminProductDetail
     */
    readonly issuanceDocument: AdminVersionedIssuanceDocument | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminProductDetail
     */
    issuancePrice?: Money | null;
    /**
     * 
     * @type {Issuer}
     * @memberof AdminProductDetail
     */
    issuer: Issuer;
    /**
     * 
     * @type {Array<Document>}
     * @memberof AdminProductDetail
     */
    readonly issuerDocuments: Array<Document>;
    /**
     * 
     * @type {Wallet}
     * @memberof AdminProductDetail
     */
    readonly issuerWallet: Wallet;
    /**
     * 
     * @type {Money}
     * @memberof AdminProductDetail
     */
    readonly latestProductPrice: Money;
    /**
     * Lock-in time of the newly created contract. This information is needed at deployment time in cases where the logic is implemented in a smart contract (e.g. Ethereum), but in any case we need a place to store it before TokenContract exists.
     * @type {Date}
     * @memberof AdminProductDetail
     */
    lockInTime?: Date | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    readonly managed: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminProductDetail
     */
    maxIssuableAmount: number;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    name: string;
    /**
     * 
     * @type {NetworkNameTechnicalEnum}
     * @memberof AdminProductDetail
     */
    network: NetworkNameTechnicalEnum;
    /**
     * 
     * @type {Money}
     * @memberof AdminProductDetail
     */
    nominalValue?: Money | null;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    readonly organization: string;
    /**
     * Deadline for the investor making the bank transfer (in days)
     * @type {number}
     * @memberof AdminProductDetail
     */
    paymentDeadline?: number;
    /**
     * 
     * @type {Array<ProductPermissionEnum>}
     * @memberof AdminProductDetail
     */
    readonly permissions: Array<ProductPermissionEnum>;
    /**
     * Type of financial product
     * @type {ProductTypeEnum}
     * @memberof AdminProductDetail
     */
    productType?: ProductTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    provideNominalValue: boolean;
    /**
     * A restricted publication is not shown on the public publications page.
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    publicationRestricted?: boolean;
    /**
     * Indicates whether the most recent publication is valid. A publication turns invalid if no investment was made during the first 3 months after the creation of the crypto security
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    readonly publicationValid: boolean | null;
    /**
     * the number of units that are untouched, i.e. neither used in invitations, fulfilled nor issued
     * @type {number}
     * @memberof AdminProductDetail
     */
    readonly remainingAmount: number;
    /**
     * Defines whether commitment requires a securities depot for investor
     * @type {boolean}
     * @memberof AdminProductDetail
     */
    securitiesDepositAccountRequired?: boolean;
    /**
     * 
     * @type {ProductStatusEnum}
     * @memberof AdminProductDetail
     */
    status?: ProductStatusEnum;
    /**
     * step size for units: investors must enter an integer multiple of the step size
     * @type {number}
     * @memberof AdminProductDetail
     */
    stepSize?: number;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    readonly tokenType: string;
    /**
     * 
     * @type {TokenContractInfo}
     * @memberof AdminProductDetail
     */
    tokenizationInfo: TokenContractInfo | null;
    /**
     * 
     * @type {number}
     * @memberof AdminProductDetail
     */
    readonly totalVolume: number;
    /**
     * 
     * @type {string}
     * @memberof AdminProductDetail
     */
    validationRejectionReason?: string;
    /**
     * Crypto securities must be validated by Cashlink before they are allowed to be distributed. validation_status describes the progress step of the validation in which the product currently is. Manual validation by Cashlink occurs while a crypto security is in step 'VALIDATION_PENDING'
     * @type {ProductValidationStatusEnum}
     * @memberof AdminProductDetail
     */
    validationStatus?: ProductValidationStatusEnum | null;
    /**
     * 
     * @type {Document}
     * @memberof AdminProductDetail
     */
    vib: Document;
    /**
     * URL to the product's website
     * @type {string}
     * @memberof AdminProductDetail
     */
    website?: string | null;
}



/**
 * Check if a given object implements the AdminProductDetail interface.
 */
export function instanceOfAdminProductDetail(value: object): value is AdminProductDetail {
    if (!('agioPercent' in value) || value['agioPercent'] === undefined) return false;
    if (!('backToBack' in value) || value['backToBack'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('currencies' in value) || value['currencies'] === undefined) return false;
    if (!('distributionPlatforms' in value) || value['distributionPlatforms'] === undefined) return false;
    if (!('entryType' in value) || value['entryType'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('investmentPaymentDeadline' in value) || value['investmentPaymentDeadline'] === undefined) return false;
    if (!('isTokenized' in value) || value['isTokenized'] === undefined) return false;
    if (!('issuanceDocument' in value) || value['issuanceDocument'] === undefined) return false;
    if (!('issuer' in value) || value['issuer'] === undefined) return false;
    if (!('issuerDocuments' in value) || value['issuerDocuments'] === undefined) return false;
    if (!('issuerWallet' in value) || value['issuerWallet'] === undefined) return false;
    if (!('latestProductPrice' in value) || value['latestProductPrice'] === undefined) return false;
    if (!('managed' in value) || value['managed'] === undefined) return false;
    if (!('maxIssuableAmount' in value) || value['maxIssuableAmount'] === undefined) return false;
    if (!('name' in value) || value['name'] === undefined) return false;
    if (!('network' in value) || value['network'] === undefined) return false;
    if (!('organization' in value) || value['organization'] === undefined) return false;
    if (!('permissions' in value) || value['permissions'] === undefined) return false;
    if (!('provideNominalValue' in value) || value['provideNominalValue'] === undefined) return false;
    if (!('publicationValid' in value) || value['publicationValid'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('tokenType' in value) || value['tokenType'] === undefined) return false;
    if (!('tokenizationInfo' in value) || value['tokenizationInfo'] === undefined) return false;
    if (!('totalVolume' in value) || value['totalVolume'] === undefined) return false;
    if (!('vib' in value) || value['vib'] === undefined) return false;
    return true;
}

export function AdminProductDetailFromJSON(json: any): AdminProductDetail {
    return AdminProductDetailFromJSONTyped(json, false);
}

export function AdminProductDetailFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminProductDetail {
    if (json == null) {
        return json;
    }
    return {
        
        'agioPercent': json['agio_percent'],
        'backToBack': DocumentFromJSON(json['back_to_back']),
        'backupWallet': json['backup_wallet'] == null ? undefined : json['backup_wallet'],
        'bankAccountRequired': json['bank_account_required'] == null ? undefined : json['bank_account_required'],
        'calculateAccruedInterest': json['calculate_accrued_interest'] == null ? undefined : json['calculate_accrued_interest'],
        'calculationFactor': json['calculation_factor'] == null ? undefined : json['calculation_factor'],
        'cancellationDeadline': json['cancellation_deadline'] == null ? undefined : json['cancellation_deadline'],
        'code': json['code'] == null ? undefined : json['code'],
        'createdAt': (new Date(json['created_at'])),
        'currencies': ((json['currencies'] as Array<any>).map(CurrencyRateFromJSON)),
        'distributionPlatforms': ((json['distribution_platforms'] as Array<any>).map(DistributionPlatformFromJSON)),
        'entryType': json['entry_type'],
        'ewpg': json['ewpg'] == null ? undefined : json['ewpg'],
        'id': json['id'],
        'investmentPaymentDeadline': json['investment_payment_deadline'],
        'isEcsp': json['is_ecsp'] == null ? undefined : json['is_ecsp'],
        'isTokenized': json['is_tokenized'],
        'isin': json['isin'] == null ? undefined : json['isin'],
        'issuanceDocument': AdminVersionedIssuanceDocumentFromJSON(json['issuance_document']),
        'issuancePrice': json['issuance_price'] == null ? undefined : MoneyFromJSON(json['issuance_price']),
        'issuer': IssuerFromJSON(json['issuer']),
        'issuerDocuments': ((json['issuer_documents'] as Array<any>).map(DocumentFromJSON)),
        'issuerWallet': WalletFromJSON(json['issuer_wallet']),
        'latestProductPrice': MoneyFromJSON(json['latest_product_price']),
        'lockInTime': json['lock_in_time'] == null ? undefined : (new Date(json['lock_in_time'])),
        'managed': json['managed'],
        'maxIssuableAmount': json['max_issuable_amount'],
        'name': json['name'],
        'network': NetworkNameTechnicalEnumFromJSON(json['network']),
        'nominalValue': json['nominal_value'] == null ? undefined : MoneyFromJSON(json['nominal_value']),
        'organization': json['organization'],
        'paymentDeadline': json['payment_deadline'] == null ? undefined : json['payment_deadline'],
        'permissions': ((json['permissions'] as Array<any>).map(ProductPermissionEnumFromJSON)),
        'productType': json['product_type'] == null ? undefined : ProductTypeEnumFromJSON(json['product_type']),
        'provideNominalValue': json['provide_nominal_value'],
        'publicationRestricted': json['publication_restricted'] == null ? undefined : json['publication_restricted'],
        'publicationValid': json['publication_valid'],
        'remainingAmount': json['remaining_amount'],
        'securitiesDepositAccountRequired': json['securities_deposit_account_required'] == null ? undefined : json['securities_deposit_account_required'],
        'status': json['status'] == null ? undefined : ProductStatusEnumFromJSON(json['status']),
        'stepSize': json['step_size'] == null ? undefined : json['step_size'],
        'tokenType': json['token_type'],
        'tokenizationInfo': TokenContractInfoFromJSON(json['tokenization_info']),
        'totalVolume': json['total_volume'],
        'validationRejectionReason': json['validation_rejection_reason'] == null ? undefined : json['validation_rejection_reason'],
        'validationStatus': json['validation_status'] == null ? undefined : ProductValidationStatusEnumFromJSON(json['validation_status']),
        'vib': DocumentFromJSON(json['vib']),
        'website': json['website'] == null ? undefined : json['website'],
    };
}

export function AdminProductDetailToJSON(value?: Omit<AdminProductDetail, 'created_at'|'currencies'|'distribution_platforms'|'entry_type'|'id'|'is_tokenized'|'issuance_document'|'issuer_documents'|'issuer_wallet'|'latest_product_price'|'managed'|'organization'|'permissions'|'publication_valid'|'remaining_amount'|'token_type'|'total_volume'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'agio_percent': value['agioPercent'],
        'back_to_back': DocumentToJSON(value['backToBack']),
        'backup_wallet': value['backupWallet'],
        'bank_account_required': value['bankAccountRequired'],
        'calculate_accrued_interest': value['calculateAccruedInterest'],
        'calculation_factor': value['calculationFactor'],
        'cancellation_deadline': value['cancellationDeadline'],
        'code': value['code'],
        'ewpg': value['ewpg'],
        'investment_payment_deadline': value['investmentPaymentDeadline'],
        'is_ecsp': value['isEcsp'],
        'isin': value['isin'],
        'issuance_price': MoneyToJSON(value['issuancePrice']),
        'issuer': IssuerToJSON(value['issuer']),
        'lock_in_time': value['lockInTime'] == null ? undefined : ((value['lockInTime'] as any).toISOString()),
        'max_issuable_amount': value['maxIssuableAmount'],
        'name': value['name'],
        'network': NetworkNameTechnicalEnumToJSON(value['network']),
        'nominal_value': MoneyToJSON(value['nominalValue']),
        'payment_deadline': value['paymentDeadline'],
        'product_type': ProductTypeEnumToJSON(value['productType']),
        'provide_nominal_value': value['provideNominalValue'],
        'publication_restricted': value['publicationRestricted'],
        'securities_deposit_account_required': value['securitiesDepositAccountRequired'],
        'status': ProductStatusEnumToJSON(value['status']),
        'step_size': value['stepSize'],
        'tokenization_info': TokenContractInfoToJSON(value['tokenizationInfo']),
        'validation_rejection_reason': value['validationRejectionReason'],
        'validation_status': ProductValidationStatusEnumToJSON(value['validationStatus']),
        'vib': DocumentToJSON(value['vib']),
        'website': value['website'],
    };
}

